import AstrologyCareerPath from "@/components/marketingPages/CareerComp";
import MarketingNewPaidForm from "@/components/marketingPages/MarketingNewPaidForm";
import ScrollEventHelper from "@/components/marketingPages/ScrollEventHelper";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import dayjs from "dayjs";
import Head from "next/head";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import { ExpandableTile } from "@/components/pages/infoTile";
import { TimerOnlyText } from "@/components/pages/timer/timer";
import { formatDate, formatTime } from "@/marketingUtils/dateTimeParser";
import { DataItem, schema } from "@/types/marketingPages/Long_Free_DT";
import mixpanel from "@/utils/mixpanel";
import DATA from "./long-free-content.json";
import fbq from "@/utils/tracker";
import { testimonials } from "../astroji-maha-shiv-puja";
import { initPixel } from "@/hooks/useAdditionalPixel";

type RaunakUiVariantProps = {
  openForm: (segment: string) => void;
  renderInlineForm?: () => React.ReactNode;
};

const RAUNAK_UI_BUCKETS = ["A", "B", "C", "D", "E", "F"] as const;
type RaunakUiBucket = (typeof RAUNAK_UI_BUCKETS)[number];

const RaunakVariantB = dynamic<RaunakUiVariantProps>(
  () => import("@/components/neo-lps/raunak-mishra-2/B/component"),
);
const RaunakVariantC = dynamic<RaunakUiVariantProps>(
  () => import("@/components/neo-lps/raunak-mishra-2/C/component"),
);
const RaunakVariantD = dynamic<RaunakUiVariantProps>(
  () => import("@/components/neo-lps/raunak-mishra-2/D/component"),
);
const RaunakVariantE = dynamic<RaunakUiVariantProps>(
  () => import("@/components/neo-lps/raunak-mishra-2/E/component"),
);
const RaunakVariantF = dynamic<RaunakUiVariantProps>(
  () => import("@/components/neo-lps/raunak-mishra-2/F/component"),
);

const RAUNAK_UI_VARIANTS: Partial<
  Record<RaunakUiBucket, ComponentType<RaunakUiVariantProps>>
> = {
  B: RaunakVariantB,
  C: RaunakVariantC,
  D: RaunakVariantD,
  E: RaunakVariantE,
  F: RaunakVariantF,
};

export async function getServerSideProps(context: {
  params: { id: string; slug: string };
}) {
  const { id, slug } = context.params;
  //@ts-ignore
  const pageData = (DATA as schema)[slug] || {};
  const abTestType =
    slug === "raunak-mishra-2" ? "raunak-mishra-2-ui-exp" : "1-3-field-lp-ab";

  try {
    const { data } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}masterclasses/${id}?abtest=true&abtesttype=${abTestType}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      },
    );

    return {
      props: {
        masterclass: data.data,
        pageData: pageData,
        slug: slug,
        abTestCounter: data.abTestCounter,
        abTestType,
      },
    };
  } catch (err) {
    return {
      props: {
        masterclass: {},
        pageData,
        slug,
        abTestCounter: 0,
        abTestType,
      },
    };
  }
}

export default function LongFree({
  masterclass,
  pageData,
  slug,
  abTestCounter,
  abTestType,
}: {
  masterclass: any;
  pageData: DataItem;
  slug: string;
  abTestCounter: number;
  abTestType?: string;
}) {
  const router = useRouter();
  const adid = router.query.adid
    ? pageData?.ad_array?.[Number(router.query.adid)]
      ? Number(router.query.adid)
      : null
    : null;
  const slots = masterclass?.slots;
  const activeSlot = slots?.find((slot: any) => slot.active);
  const activeDate = formatDate(activeSlot?.startDateTime);
  const activeTimeSlot = formatTime(activeSlot?.startDateTime);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const is2FieldSlug = [
    "mahesh-lalkitab",
    "vaibhav-bnn",
    "krish-kp",
    "tarun-regression",
  ].includes(slug);
  const is1FieldLp: "A" | "B" = abTestCounter % 2 == 1 ? "B" : "A";
  const isCounter50: "A" | "B" = abTestCounter % 2 == 1 ? "B" : "A";
  const isRaunakUiExperiment = slug === "raunak-mishra-2";
  const requestedBucket =
    typeof router.query.bucket === "string"
      ? router.query.bucket.toUpperCase()
      : "";
  const raunakUiBucket: RaunakUiBucket = RAUNAK_UI_BUCKETS.includes(
    requestedBucket as RaunakUiBucket,
  )
    ? (requestedBucket as RaunakUiBucket)
    : RAUNAK_UI_BUCKETS[abTestCounter % RAUNAK_UI_BUCKETS.length];
  const analyticsBucket = isRaunakUiExperiment ? raunakUiBucket : isCounter50;
  const handleLeadFormOpen = (segment: string) => {
    setIsLeadFormOpen(true);
    //@ts-ignore
    if (fbq) {
      //@ts-ignore
      fbq("trackSingleCustom", `Register Now Button Clicked`);
    }
    mixpanel.track("enroll_now", {
      type: "free",
      landing_page: "long-free",
      masterclass: masterclass?.title,
      masterclassId: masterclass?.id,
      platform: "astrolearn",
      segment: "long_free_" + segment,
    });
  };
  const mobileFormRef = useRef<HTMLDivElement>(null);

  const handleMobileRegisterClick = (segment: string) => {
    //@ts-ignore
    if (fbq) {
      //@ts-ignore
      fbq("trackSingleCustom", `Register Now Button Clicked`);
    }
    mixpanel.track("enroll_now", {
      type: "free",
      landing_page: "long-free",
      masterclass: masterclass?.title,
      masterclassId: masterclass?.id,
      platform: "astrolearn",
      segment: "long_free_" + segment,
      activeSlot: activeSlot?.id,
      activeWaGroup: activeSlot?.whatsappGroupLink,
      slug: slug,
      startDateTime: activeSlot?.startDateTime,
    });
    if (mobileFormRef.current) {
      mobileFormRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const HideTimerArray = ["saurav-bnn-taboola"];

  useEffect(() => {
    try {
      mixpanel.track("marketing_page", {
        page_location: window.location.href,
        page_referrer: document.referrer,
        masterclass: masterclass?.title,
        type: "free",
        landing_page: "long-free",
        masterclassId: masterclass?.id,
        platform: "astrolearn",
        activeSlot: activeSlot?.id,
        activeWaGroup: activeSlot?.whatsappGroupLink,
        slug: slug,
        startDateTime: activeSlot?.startDateTime,
        abtesttype: abTestType,
        bucket: analyticsBucket,
      });
      mixpanel.track("marketing_ab_test", {
        platform: "astrolearn",
        masterclassId: masterclass?.id,
        landing_page: "long-free",
        abtesttype: abTestType,
        bucket: `${analyticsBucket}`,
        activeSlot: activeSlot?.id,
        activeWaGroup: activeSlot?.whatsappGroupLink,
        slug: slug,
        startDateTime: activeSlot?.startDateTime,
      });
    } catch (error) {}
  }, []);
  const [price, setPrice] = useState<PriceObj>({
    price: masterclass.price,
    discountedPrice: "0",
    workshopTitle: masterclass.title,
    source: "free_masterclass",
  });
  const source = router.query.source;
  const fulldomain = process.env.NEXT_PUBLIC_FULLDOMAIN;
  const hideCertificateArray = [
    "tarun-regression",
    "tarun-hypnosis",
    "aarti-ahuja",
    "nehha-hooponopono",
    "ameeta-diwali",
    "pravin-deepawali",
    "mahesh-aghori",
  ];
  const hideCertificate = hideCertificateArray.includes(slug);
  const isAdditionalPixelOnPage = [
    "shhalu-ahuja",
    "shhalu-ahuja-tarot",
    "rema-sangwan",
    "vivek-khanna",
    "raunak-mishra",
    "garrima-prakaesh",
    "acharya-laxmi-tripathi",
    "venughopaal-nlp",
    "sandhya-k-bassi",
    "sandhya-k-bassi-vedic",
    "gargi-dhawan-mradul",
    "pooja-dawar",
    "amritaa-shankar",
  ].includes(slug);

  const SelectedRaunakVariant = RAUNAK_UI_VARIANTS[raunakUiBucket];

  if (isRaunakUiExperiment && SelectedRaunakVariant) {
    return (
      <>
        <Head>
          <title>{pageData?.title}</title>
          <meta
            name={pageData?.meta?.name}
            content={pageData?.meta?.content}
            key={pageData?.meta?.key}
          />
        </Head>
        {isAdditionalPixelOnPage ? initPixel(slug) : null}
        <ScrollEventHelper landing_page="long-free" masterclass={masterclass} />
        <button
          id="global-open-form-btn"
          className="hidden"
          onClick={() => handleLeadFormOpen("hero")}
        />
        <SelectedRaunakVariant
          openForm={handleLeadFormOpen}
          renderInlineForm={() => (
            <MarketingNewPaidForm
              price={price}
              masterclass={masterclass}
              isFreeMc={true}
              type="normal"
              slug={slug}
              bucket={raunakUiBucket}
              abTestType={abTestType}
              customCtaText="Register Now for Free"
              btnClass="!bg-[#FF7700]"
              showEmailField={false}
              showNameField={false}
              showPhoneLabel={false}
              {...(isAdditionalPixelOnPage && {
                additionalPixelEventToFiredOnLead: {
                  eventName: "Lead",
                  eventType: "standard" as const,
                },
              })}
              formHeading=""
            />
          )}
        />
        <MarketingNewPaidForm
          isOpen={isLeadFormOpen}
          closeModal={() => setIsLeadFormOpen(false)}
          price={price}
          masterclass={masterclass}
          isFreeMc={true}
          type="modal"
          slug={slug}
          bucket={raunakUiBucket}
          abTestType={abTestType}
          customCtaText="Register Now for Free"
          btnClass="!bg-[#FF7700]"
          showEmailField={false}
          showNameField={false}
          showPhoneLabel={false}
          {...(isAdditionalPixelOnPage && {
            additionalPixelEventToFiredOnLead: {
              eventName: "Lead",
              eventType: "standard" as const,
            },
          })}
        />
      </>
    );
  }

  return (
    <>
      <Head>
        <title>{pageData?.title}</title>
        <meta
          name={pageData?.meta?.name}
          content={pageData?.meta?.content}
          key={pageData?.meta?.key}
        />
      </Head>
      {isAdditionalPixelOnPage ? initPixel(slug) : null}
      <main className="w-full pb-24 ">
        {pageData?.guruName && (
          <div className="fixed top-2 right-2 z-30 text-xs font-bold text-gray-700">
            {pageData?.guruName}
          </div>
        )}
        <button
          onClick={() => handleLeadFormOpen("footer")}
          className={`bg-[#FF7700] md:py-3 !text-white ${"max-md:hidden"}  flex-row  gap-1 items-center justify-center hover:scale-105 fixed left-0 bottom-0 w-full h-[56px] z-20 text-xl`}
        >
          <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md " />
          Register Now For Free <ArrowRightIcon className="w-5 h-5" />
        </button>
        <ScrollEventHelper landing_page="long-free" masterclass={masterclass} />
        {/* <Modal
          className="md:w-1/2"
          isOpen={isLeadFormOpen}
          closeModal={() => {
            setIsLeadFormOpen(false);
          }}
        >
          <LeadForm
            masterClass={masterclass}
            startDateTime={activeSlot?.startDateTime}
            type={page_type.FREE}
            landing_page={landing_page_type.LONG}
          />
        </Modal> */}
        <MarketingNewPaidForm
          isOpen={isLeadFormOpen}
          closeModal={() => setIsLeadFormOpen(false)}
          price={price}
          masterclass={masterclass}
          isFreeMc={true}
          type="modal"
          slug={slug}
          {...(isRaunakUiExperiment && {
            bucket: raunakUiBucket,
            abTestType,
          })}
          customCtaText="Register Now for Free"
          btnClass="!bg-[#FF7700]"
          showEmailField={false}
          showNameField={false}
          showPhoneLabel={false}
          {...(isAdditionalPixelOnPage && {
            additionalPixelEventToFiredOnLead: {
              eventName: "Lead",
              eventType: "standard",
            },
          })}
        />
        {/* phone view */}
        <div className="h-auto relative overflow-hidden flex items-center justify-center md:hidden flex-col">
          <div className="flex relative justify-center items-center flex-col px-5">
            <div className="absolute w-[150%] overflow-hidden rounded-b-[45%] h-full bg-[#FFF3E4] -z-20">
              <img
                src="/astro/astro-chakra.png "
                className="w-[35%] mt-40 animate-spin-slow absolute -z-10 ml-52 sm:ml-80"
              />
            </div>
            {pageData?.company === "" ? null : pageData?.company ? (
              <div className="flex gap-1 mt-4">
                <img src={`/${slug}/logo.webp`} width={30} height={30} />
                <p className="text-[2rem] align-middle leading-normal font-[200] max-xl:text-xl ">
                  {pageData?.company}
                </p>
              </div>
            ) : (
              <div className="flex gap-1 mt-4">
                <img src="/astro/astro-logo-dark.svg" width={30} height={30} />
                <p className="text-[2rem] align-middle leading-normal font-[200] max-xl:text-xl ">
                  AstroLearn
                </p>
              </div>
            )}
            <div className=" text-center  pt-2">
              <span
                className="px-2 text-center font-sans font-bold text-xl text-[#2F327D]"
                dangerouslySetInnerHTML={{
                  __html: adid
                    ? pageData?.ad_array
                      ? pageData?.ad_array?.[adid].heading
                        ? pageData?.ad_array?.[adid].heading
                        : pageData?.heading
                      : pageData?.heading
                    : pageData?.heading,
                }}
              ></span>
              <div className="mt-0 px-2">
                <span
                  className="text-[#464646] text-[14px] leading-6 font-[400] font-nunito tracking-wide"
                  dangerouslySetInnerHTML={{
                    __html: pageData?.below_heading_line,
                  }}
                />
              </div>
              <div className=" relative flex flex-col justify-center items-center mt-[20px] ">
                <img
                  src={`/${slug}/teacher.webp`}
                  alt="photo of the teacher"
                  className="w-[80%] z-10"
                />
              </div>
            </div>
            <img
              src="/ui/side-navbar/review.svg"
              className=" z-20 mt-[-40px]"
              alt=" review people"
            />
          </div>
        </div>
        <div className="flex justify-center mt-[1vh] md:hidden">
          <div className="flex flex-row gap-x-2 p-2 rounded-md">
            <div className="bg-[#FBE0C7] flex flex-row  gap-2 justify-center items-center text-center px-6 py-1 rounded-md min-w-[152px]">
              <div
                className={`bg-[#FF7700] !aspect-square w-[30px] flex justify-center items-center rounded-xl `}
              >
                <img src="/ui/side-navbar/new-cal.svg" width={20} />
              </div>
              <span className="font-semibold">{activeDate}</span>
            </div>
            <div className="bg-[#FBE0C7] flex flex-row items-center gap-2 justify-center text-center px-6 py-1 rounded-md min-w-[152px]">
              <div
                className={`bg-[#FF7700] !aspect-square w-[30px] flex justify-center items-center rounded-xl `}
              >
                <img src="/ui/side-navbar/time.svg" width={20} />
              </div>
              <span className="font-semibold">{activeTimeSlot}</span>
            </div>
          </div>

          <div className="max-sm:fixed sm:hidden bottom-0 left-0 z-[30] right-0 w-full border-dashed">
            <MarketingNewPaidForm
              price={price}
              masterclass={masterclass}
              isFreeMc={true}
              type="normal"
              showEmailField={false}
              showNameField={false}
              showPhoneLabel={false}
              slug={slug}
              outerContainerClass="!py-3"
              customCtaText="Register Now for Free"
              btnClass="!bg-[#FF7700]"
              formHeading=""
              {...(isAdditionalPixelOnPage && {
                additionalPixelEventToFiredOnLead: {
                  eventName: "Lead",
                  eventType: "standard",
                },
              })}
            />
          </div>
        </div>
        {/* desktop view */}
        <div className="max-md:hidden ">
          <div className="fixed max-sm:hidden bottom-0 z-30 lg:px-[110px] p-[20px] w-full flex justify-between items-center bg-white shadow-2xl">
            <div>
              <div className="animate-pulse">
                <b className="text-orange-500 text-3xl">FREE</b>{" "}
                <span className=" line-through text-xs text-[#7B7D7C]">
                  ₹999
                </span>
              </div>
              {!HideTimerArray.includes(slug) && (
                <div className="text-xs text-[#7B7D7C]">
                  Offer Expires in{" "}
                  <b className="text-black">
                    <TimerOnlyText
                      tillDateTime={dayjs().add(15, "minute").toDate()}
                    />
                  </b>
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => handleLeadFormOpen("footer")}
                className="bg-[#FF7700] md:py-3 !text-white md:text-lg rounded-lg max-sm:hidden flex flex-row  gap-1 items-center justify-center hover:scale-105 relative"
              >
                <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
                Register Now For Free <ArrowRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="relative overflow-hidden flex justify-center items-center px-[6rem]">
            <div className="absolute w-[150%] overflow-hidden rounded-b-[50%] h-full bg-[#FFF3E4] ">
              <img
                src="/astro/astro-chakra.png"
                className="animate-spin-slow duration-[5s] ease-linear w-[25%] absolute  right-[22%] top-[10%]"
              ></img>
              <img
                src={`/${slug}/teacher.webp`}
                className="z-20 w-[25%] mt-36 absolute right-[23%] "
              ></img>
            </div>
            <div className="relative w-[70%] lg:w-1/2 max-lg:-ml-20 py-5">
              {pageData?.company === "" ? null : pageData?.company ? (
                <div className="flex gap-1 items-center">
                  <Image
                    width={36}
                    height={36}
                    src={`/${slug}/logo.webp`}
                    alt="icon"
                    className="max-xl:w-8 max-xl:h-8"
                  />
                  <span className="text-[2rem] leading-normal font-[200] max-xl:text-xl">
                    {pageData?.company}
                  </span>
                </div>
              ) : (
                <div className="flex gap-1 items-center">
                  <Image
                    width={36}
                    height={36}
                    src="/astro/astro-logo-dark.svg"
                    alt="icon"
                    className="max-xl:w-8 max-xl:h-8"
                  />
                  <span className="text-[2rem] leading-normal font-[200] max-xl:text-xl">
                    AstroLearn
                  </span>
                </div>
              )}
              <div className="mt-3 xl:mt-[3.5rem]">
                <div
                  className="font-sans font-bold text-[2.5rem] text-[#2F327D] max-xl:text-2xl"
                  dangerouslySetInnerHTML={{ __html: pageData?.heading }}
                ></div>
                <div className="leading-4">
                  <span
                    className="text-[#464646] text-[1.1srem] leading-7 font-[350] font-nunito tracking-wider flex-wrap"
                    dangerouslySetInnerHTML={{
                      __html: pageData?.below_heading_line,
                    }}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-[1rem]">
                <div className="bg-[#FBE0C7] flex flex-row gap-2 items-center  px-4 py-2 rounded-lg font-semibold max-xl:px-2 w-44 max-xl:w-36">
                  <div
                    className={`bg-[#FF7700] !aspect-square w-[35px] flex justify-center items-center rounded-xl `}
                  >
                    <img src="/ui/side-navbar/new-cal.svg" />
                  </div>
                  <div className=" flex flex-col items-start">
                    <p className=" font-[400] text-xs">DATE</p>
                    <span className=" uppercase  font-bold  text-[13px]">
                      {activeDate}
                    </span>
                  </div>
                </div>
                <div className="bg-[#FBE0C7] flex flex-row gap-2 items-center  px-4 py-2 rounded-lg font-semibold max-xl:px-2 w-44 max-xl:w-36">
                  <div
                    className={`bg-[#FF7700] !aspect-square w-[35px] flex justify-center items-center rounded-xl `}
                  >
                    <img src="/ui/side-navbar/time.svg" />
                  </div>
                  <div className=" flex flex-col">
                    <p className=" font-[400] text-xs">TIME</p>
                    <span className=" uppercase  font-bold  text-[13px]">
                      {activeTimeSlot}
                    </span>
                  </div>
                </div>
                <div className="bg-[#FBE0C7] flex gap-2 items-center  px-4 py-2 rounded-lg font-semibold max-xl:px-2 w-44 max-xl:w-36">
                  <div
                    className={`bg-[#FF7700] !aspect-square w-[35px] flex justify-center items-center rounded-xl `}
                  >
                    <img src="/ui/side-navbar/globe.svg" />
                  </div>
                  <div className=" flex flex-col items-start">
                    <p className=" font-[400] text-xs">LANGUAGE</p>
                    <span className=" uppercase  font-bold  text-[12px]">
                      {pageData?.language}
                    </span>
                  </div>
                </div>
                <div className="bg-[#FBE0C7] flex gap-2 items-center  px-4 py-2 rounded-lg font-semibold max-xl:px-2 w-44 max-xl:w-36">
                  <div
                    className={`bg-[#FF7700] !aspect-square w-[35px] flex justify-center items-center rounded-xl `}
                  >
                    <img src="/ui/side-navbar/sand-clock.svg" />
                  </div>
                  <div className=" flex flex-col">
                    <p className=" font-[400] text-xs">DURATION</p>
                    <span className=" uppercase  font-bold  text-[12px]">
                      2+ Hours
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex mt-[2rem] gap-3 items-center">
                <button
                  className="bg-[#F48C06] xl:px-[1.3rem] xl:py-[0.5rem] !text-white font-semibold tracking-wider rounded-half xl:text-lg text-sm py-4 px-4 relative max-sm:hidden"
                  onClick={() => handleLeadFormOpen("hero")}
                >
                  <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
                  Register Now For Free
                </button>
                <img src="/ui/side-navbar/review.svg" alt="review people" />
                <div>
                  <div className="flex flex-row items-center">
                    <img src="/common/star.png" className="w-5 h-5" />
                    <img src="/common/star.png" className="w-5 h-5" />
                    <img src="/common/star.png" className="w-5 h-5" />
                    <img src="/common/star.png" className="w-5 h-5" />
                    <img src="/common/star.png" className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-semibold mt-[-4px]">
                    14k+ reviews (4.9 of 5)
                  </span>
                </div>
              </div>
            </div>
            <div className=" w-full">
              <img
                src={`/krishcommon.webp`}
                className=" opacity-0 mt-36 xl:w-[80%] h-[240px] lg:h-[380px] xl:h-[475px]"
              ></img>
            </div>
          </div>
        </div>
        {/* ----------------------------------------------------------------- */}
        <div className="flex flex-col justify-center items-center px-24 md:py-16 py-4 max-md:px-6 md:gap-12 gap-6 ">
          <h1 className="text-[#2F327D] xl:text-[45px] max-md:text-2xl">
            {pageData.sectionHeading?.heading1 ?? "What Will You Learn?"}
          </h1>
          <div className="grid grid-cols-3 gap-7 justify-center w-full max-md:grid-cols-2 max-md:gap-4">
            {pageData?.learning_outcome?.map((outcome, index) => {
              return (
                <div
                  className="bg-[#FFF3E4] flex flex-col gap-2 justify-center items-center p-5 rounded-lg"
                  key={index}
                >
                  <img src={`/${slug}/icon${index}.svg`} width={50} />
                  <h4 className="text-[#F48C06] max-md:text-base text-center">
                    {outcome?.title}
                  </h4>
                  <p className="max-md:text-sm text-pretty text-center">
                    {outcome?.description}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col max-md:gap-3 gap-8 w-full justify-center items-center">
            <button
              className={`bg-[#F48C06] !text-white w-full py-5 max-md:text-xl text-lg ${"max-md:hidden"} relative`}
              onClick={() => handleLeadFormOpen("learning_outcome")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
            {slug == "chaitanyaa-vaastu-tool" ? (
              <div className="w-full max-sm:hidden">
                <AstrologyCareerPath />
              </div>
            ) : null}
            <button
              className="bg-[#F48C06] !text-white w-full py-5 max-md:text-xl text-lg  hidden relative"
              onClick={() => handleLeadFormOpen("learning_outcome")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
          </div>
        </div>
        <div
          className="flex flex-col justify-center items-center px-24 py-16 max-md:px-10 max-md:py-8 gap-12 bg-no-repeat bg-opacity-5 max-md:bg-left"
          style={{
            backgroundImage: 'url("/long-page/flower-left.svg")',
          }}
        >
          <h1 className="text-[#2F327D] xl:text-[45px] max-md:text-2xl text-center md:leading-[50px]">
            {pageData?.question}
          </h1>
          <div className="flex flex-col gap-2">
            {pageData?.answers?.map((answer, index) => {
              return (
                <div className="flex flex-row gap-2" key={index}>
                  <img src="/long-page/right-arrow.svg" alt="side arrow" />
                  <h4 className="tracking-wider">{answer}</h4>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col max-md:gap-3 gap-8 w-full justify-center items-center">
            <button
              className={`bg-[#F48C06] !text-white w-full py-5 max-md:text-xl text-lg ${"max-md:hidden"}   relative`}
              onClick={() => handleLeadFormOpen("why_this_mc")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
            <button
              className="bg-[#F48C06] !text-white w-full py-5 max-md:text-xl text-lg hidden relative"
              onClick={() => handleLeadFormOpen("why_this_mc")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
          </div>
        </div>
        <div
          className="flex flex-col items-center justify-center lg:px-4 bg-[#F48C06] bg-opacity-5 py-3  "
          style={{
            backgroundImage:
              'url("/long-page/final_line_sliced.png") opacity:0.5',
          }}
        >
          <div className="flex flex-row items-center justify-center lg:gap-20 max-md:gap-10 max-md:flex-wrap">
            <div
              className="relative bg-cover bg-center w-[200px] h-[300px] my-10 "
              style={{
                backgroundImage: ` url('/common/tajoutline.svg')`,
              }}
            >
              <div
                className=" z-50"
                style={{
                  backgroundImage: ` url('/long-page/tajImg.png')`,
                }}
              ></div>
              <img
                src="/astro/astro-chakra.png"
                className=" absolute  top-14 translate-x-1 w-[70%] ml-7 -z-20"
              />
              <img
                className=" absolute bottom-1 w-[200px] -z-10"
                src={`/${slug}/teacher.webp`}
              />
            </div>
            <div className="flex flex-col justify-center items-center w-[400px] max-md:w-full max-md:px-2 gap-4 text-sm">
              <h1 className="text-[#2F327D]">
                {pageData.sectionHeading?.heading2 ?? "Meet Your Coach"}
              </h1>
              <p className="text-[#F48C06] text-lg font-bold">
                {pageData?.teacher?.name}
              </p>
              <p className="font-semibold text-lg text-center">
                {pageData?.teacher?.teacher_heading}
              </p>
              <p
                className="text-center  lg:px-4 text-wrap"
                dangerouslySetInnerHTML={{
                  __html: pageData?.teacher?.teacher_description,
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-3 items-center justify-center">
            <div className="flex flex-row gap-4 max-md:flex-wrap items-center justify-center">
              {pageData?.teacher_achivement?.map((achiveMentObj, index) => {
                return (
                  <div
                    className=" relative lg:w-[242px] max-md:w-[170px] min-h-[163px] shadow-lg rounded-lg flex flex-col gap-3 py-7 px-3"
                    key={index}
                  >
                    <h3>{achiveMentObj.heading}</h3>
                    <p className=" font-montserrat text-sm md:text-base mt-2 ">
                      {achiveMentObj.description}
                    </p>
                    <img
                      src={`/common/longfree${index}.svg`}
                      className="absolute right-2 bg-[#F48C06] rounded-lg p-2 shadow-lg"
                    />
                  </div>
                );
              })}
            </div>
            <button
              className={`text-bold bg-[#F48C06] max-md:mx-4 w-[90%] md:w-full rounded-md !text-white h-12 ${"max-md:hidden"} relative`}
              onClick={() => handleLeadFormOpen("about_teacher")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
            <button
              className="text-bold bg-[#F48C06] max-md:mx-4 w-[90%] md:w-full rounded-md !text-white h-12 hidden relative"
              onClick={() => handleLeadFormOpen("about_teacher")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
          </div>
        </div>

        {pageData?.app && (
          <div className="flex flex-col items-center justify-center gap-6 md:gap-8 py-12 md:py-16 px-4 md:px-24 bg-gradient-to-b from-white to-[#FFF3E4]">
            <div className="text-center">
              <h2 className="text-[#2F327D] text-2xl md:text-4xl font-bold mb-2">
                {pageData.app.heading}
              </h2>
              <p className="text-[#F48C06] text-xl md:text-3xl font-bold tracking-wide">
                {pageData.app.description}
              </p>
            </div>

            {/* Mobile Slider */}
            <div className="md:hidden w-full mt-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
              <div className="flex gap-4 px-4 pb-4">
                {pageData.app.image_url?.map((img, idx) => (
                  <div
                    className="snap-center shrink-0 flex justify-center items-center "
                    key={idx}
                  >
                    <img
                      src={img}
                      alt={`App Screenshot ${idx + 1}`}
                      className="w-[250px] h-auto rounded-lg shadow-xl"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop Grid */}
            <div className="hidden md:grid grid-cols-3 gap-6 md:gap-8 w-full max-w-4xl mt-4">
              {pageData.app.image_url?.map((img, idx) => (
                <div className="flex justify-center items-center" key={idx}>
                  <img
                    src={img}
                    alt={`App Screenshot ${idx + 1}`}
                    className="w-full max-w-[250px] h-auto rounded-lg shadow-xl hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>

            <button
              className={`bg-[#F48C06] !text-white py-4 px-8 md:py-5 w-full md:w-auto md:min-w-[400px] text-lg md:text-xl rounded-lg font-semibold ${"max-md:hidden"} relative mt-4 hover:bg-[#e07d00] transition-colors`}
              onClick={() => handleLeadFormOpen("gold_tree_software")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-lg" />
              Register Now For Free
            </button>
          </div>
        )}

        {pageData.sectionHeading?.heading3 && (
          <div className="flex flex-col items-center justify-center gap-3 md:mt-20 mt-6">
            <h1 className="text-[#2F327D] max-md:">
              {pageData.sectionHeading?.heading3 ?? "Unable to decide?"}
            </h1>
            <div className="border-2 border-[#F48C06] w-28 flex flex-row justify-center"></div>
            <p className="font-semibold text-center">
              Listen Stories Of Success From Other People I have Worked With
            </p>
            <div className="flex flex-row max-md:flex-wrap items-center justify-center gap-3">
              {pageData?.video_embades_urls?.map((url, index) => {
                return (
                  <iframe
                    width="350"
                    height="200"
                    src={url}
                    key={index}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  >
                    {" "}
                  </iframe>
                );
              })}
            </div>
          </div>
        )}
        {pageData.testimonials && (
          <div className="flex flex-col items-center justify-center px-10 mt-9 max-md:gap-10">
            <p className="font-bold text-center px-3">
              Here are some Real Feedbacks from students
            </p>
            <div className="flex flex-row gap-16 justify-center p-12 max-sm:flex-wrap max-sm:p-2">
              <div className="relative flex flex-col items-center justify-center shadow-xl p-8 rounded-xl gap-5 border-2 max-w-md max-xl:w-80 max-sm:w-80">
                <img
                  src="/long-page/testimonials/person1.png"
                  className="z-10 -mt-20"
                />
                <p className="text-center">
                  {pageData?.testimonials?.[0]?.description}
                </p>
                <b className="text-[#F48C06]">
                  {pageData?.testimonials?.[0]?.name}
                </b>
              </div>
              <div className="relative flex flex-col items-center justify-center shadow-xl p-8 rounded-xl gap-5 border-2 max-w-md max-xl:w-80">
                <img
                  src="/long-page/testimonials/person2.png"
                  className="z-10 -mt-20"
                />
                <p className="text-center">
                  {pageData?.testimonials?.[1]?.description}
                </p>
                <b className="text-[#F48C06]">
                  {pageData?.testimonials?.[1]?.name}
                </b>
              </div>
            </div>

            <div className="relative flex flex-row gap-16 justify-center p-12 max-sm:flex-wrap max-sm:p-2">
              <div className="flex flex-col items-center justify-center shadow-xl p-8 rounded-xl gap-2 border-2 max-w-md max-xl:w-80">
                <img
                  src="/long-page/testimonials/person3.png"
                  className="z-10 -mt-20"
                />
                <p className="text-center">
                  {pageData?.testimonials?.[2]?.description}
                </p>
                <b className="text-[#F48C06]">
                  {pageData?.testimonials?.[2]?.name}
                </b>
              </div>
              <div className="relative flex flex-col items-center justify-center shadow-xl p-8 rounded-xl gap-5 border-2 max-w-md max-xl:w-80 ">
                <img
                  src="/long-page/testimonials/person4.png"
                  className="z-10 -mt-20"
                />
                <p className="text-center">
                  {pageData?.testimonials?.[3]?.description}
                </p>
                <b className="text-[#F48C06]">
                  {pageData?.testimonials?.[3]?.name}
                </b>
              </div>
            </div>
          </div>
        )}

        {!hideCertificate && (
          <div
            className="flex justify-center items-center p-10 flex-col gap-10 bg-no-repeat bg-[#F48C06] bg-opacity-5 "
            style={{
              backgroundImage: 'url("/long-page/flower-left.svg")',
              backgroundPosition: "left",
            }}
          >
            <div>
              <h2 className="font-bold max-md:text-lg text-center">
                Valuable Certificate upon completion of the Masterclass!
              </h2>
              <div className=" w-28 h-1 bg-[#F48C06] mx-auto mt-3"></div>
            </div>

            <img src={`/${slug}/certificate.webp`} width={250}></img>
            <button
              className={`bg-[#F48C06] !text-white py-3 w-[800px] max-lg:w-80 text-lg ${"max-md:hidden"} relative`}
              onClick={() => handleLeadFormOpen("certificate")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
            <button
              className="bg-[#F48C06] !text-white py-3 w-[800px] max-lg:w-80 text-lg hidden relative"
              onClick={() => handleLeadFormOpen("certificate")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              Register Now For Free
            </button>
          </div>
        )}
        <div className="mt-10">
          <h2 className="text-[21px] md:text-3xl text-center">
            Frequently Asked <span className="text-[#F48C06]">Questions</span>
          </h2>
          <div className=" w-14 h-1 bg-[#F48C06] mx-auto my-5"></div>
          <div className="w-full flex flex-col gap-5 items-center px-2">
            {pageData?.FAQ_Content?.map((faq, index) => {
              return (
                <ExpandableTile header={faq.question} key={index}>
                  {faq.answer}
                </ExpandableTile>
              );
            })}
          </div>
          <div className="max-sm:block hidden h-44" />
        </div>
      </main>
    </>
  );
}
